import type { Control } from "react-hook-form";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Typography } from "@mui/material";
import { FormAccordionSection } from "Components/Shared/FormAccordionSection";
import { CurrencyAdornment } from "Components/Shared/Inputs/CurrencyAdornment";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { FacilityFormModel } from "Components/Shared/Merchant/Facilities/schema";
import type { CurrencyCode } from "Models/CurrencyCodes";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;

type Props = {
  control: Control<FacilityFormModel>;
  isExpanded: boolean;
  onToggle: () => void;
  isComplete: boolean | undefined;
  isIncompleteIconHidden: boolean;
  hasErrors: boolean;
  // Currency the turnover figures are entered in — the facility's stored
  // TurnoverCurrencyCode, or the merchant's market currency for a new facility.
  // Must match what the backend stamps on the row, so it is never guessed here.
  currencyCode: CurrencyCode | string | undefined;
};

export const TurnoverSection = ({
  control,
  isExpanded,
  onToggle,
  isComplete,
  isIncompleteIconHidden,
  hasErrors,
  currencyCode,
}: Props) => {
  const { t } = useResource();
  const currencyAdornment = <CurrencyAdornment currencyCode={currencyCode} />;

  return (
    <FormAccordionSection
      legend={t(RESOURCES.Legend.Turnover)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isComplete={isComplete}
      hideIncompleteIcon={isIncompleteIconHidden}
      hasErrors={hasErrors}
    >
      <FormInput
        control={control}
        name="yearlyTurnover"
        label={t(RESOURCES.Fields.YearlyTurnover)}
        type="number"
        slotProps={{
          input: {
            endAdornment: currencyAdornment,
          },
        }}
      />

      <FormInput
        control={control}
        name="realCardTurnover"
        label={t(RESOURCES.Fields.RealCardTurnover)}
        type="number"
        slotProps={{
          input: {
            endAdornment: currencyAdornment,
          },
        }}
      />
      <FormInput
        control={control}
        name="averageTransactionValue"
        label={t(RESOURCES.Fields.AverageTransactionValue)}
        type="number"
        slotProps={{
          input: {
            endAdornment: currencyAdornment,
          },
        }}
      />
      <FormInput
        control={control}
        name="estimatedTurnover"
        label={t(RESOURCES.Fields.EstimatedTurnover)}
        type="number"
        slotProps={{
          input: {
            endAdornment: currencyAdornment,
          },
        }}
        helperText={
          <Typography
            variant="caption"
            display="flex"
            alignItems="center"
            mt={1}
            gap={1}
          >
            <InfoOutlinedIcon
              sx={{ height: "1rem", width: "1rem", mt: "-2px" }}
            />
            {t(RESOURCES.Fields.EstimatedTurnoverHelperText)}
          </Typography>
        }
      />
    </FormAccordionSection>
  );
};
